import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Alert, CircularProgress, Chip, Divider, IconButton, List, ListItem,
  ListItemText, ListItemIcon, Card, CardContent, Grid, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import ShareIcon from '@mui/icons-material/Share';
import { getReferralCode, sendReferralInvite, getReferralStats } from '../api/referrals';

export default function ReferralDialog({ open, onClose }) {
  const [loading, setLoading] = useState(true);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (open) {
      loadReferralData();
    }
  }, [open]);

  async function loadReferralData() {
    setLoading(true);
    try {
      const [codeRes, statsRes] = await Promise.all([
        getReferralCode(),
        getReferralStats()
      ]);
      
      const code = codeRes.data?.referralCode || codeRes?.referralCode;
      setReferralCode(code);
      
      const baseUrl = window.location.origin;
      setReferralLink(`${baseUrl}/sign-up?ref=${code}`);
      
      setStats(statsRes.data || statsRes);
    } catch (err) {
      console.error('Error loading referral data:', err);
      setError('Failed to load referral information');
    } finally {
      setLoading(false);
    }
  }

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSendInvite = async () => {
    if (!email) {
      setError('Please enter an email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setSending(true);
    setError('');
    setSuccess('');

    try {
      await sendReferralInvite(email);
      setSuccess(`Invitation sent successfully to ${email}!`);
      setEmail('');
      // Refresh stats
      const statsRes = await getReferralStats();
      setStats(statsRes.data || statsRes);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableScrollLock>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={600}>
              Refer & Earn
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            {/* Rewards Info Banner */}
            <Paper 
              sx={{ 
                p: 3, 
                mb: 3, 
              }}
            >
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Earn ₹50 Per Referral
                  </Typography>
                  <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>
                    1 Credit = ₹1 | Redeemable in payment cycles
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{my: 2}} />
              
              <Typography variant="body2" sx={{ mb: 1, color: 'text.primary' }}>
                <strong>How it works:</strong>
              </Typography>
              <List dense>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="1. Share your unique referral link with others"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="2. When they register using your link, you earn credits"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.5 }}>
                  <ListItemText 
                    primary="3. Redeem credits during payment cycles to reduce costs"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
              <Divider sx={{my: 2}} />
            </Paper>

            {/* Stats Cards */}
            {stats && (
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={700} color="primary">
                        ₹{stats.credits?.available || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Available Credits
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={700} color="success.main">
                        {stats.completedReferrals || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Successful Referrals
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={700} color="warning.main">
                        {stats.pendingReferrals || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Pending Invites
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight={700}>
                        {stats.totalReferrals || 0}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Referrals
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {/* Referral Link */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Your Referral Link
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  value={referralLink}
                  fullWidth
                  size="small"
                  InputProps={{ readOnly: true }}
                  sx={{ fontFamily: 'monospace' }}
                />
                <Button
                  variant="contained"
                  onClick={handleCopyLink}
                  startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </Box>
            </Box>

            {/* Send Invitation */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Send Invitation via Email
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  fullWidth
                  size="small"
                  type="email"
                  disabled={sending}
                />
                <Button
                  variant="outlined"
                  onClick={handleSendInvite}
                  disabled={sending}
                  startIcon={sending ? <CircularProgress size={16} /> : <EmailIcon />}
                  sx={{ minWidth: 120 }}
                >
                  {sending ? 'Sending...' : 'Send'}
                </Button>
              </Box>
            </Box>

            {/* Success/Error Messages */}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {/* Recent Referrals */}
            {stats?.recentReferrals && stats.recentReferrals.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} mb={1}>
                  Recent Referrals
                </Typography>
                <List dense>
                  {stats.recentReferrals.slice(0, 5).map((ref, idx) => (
                    <ListItem 
                      key={idx}
                      sx={{ 
                        bgcolor: '#f5f5f5', 
                        mb: 1, 
                        borderRadius: 1,
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <ListItemIcon>
                        {ref.status === 'completed' ? (
                          <CheckCircleIcon color="success" />
                        ) : (
                          <PendingIcon color="warning" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={ref.refereeEmail}
                        secondary={
                          <Box component="span" display="flex" alignItems="center" gap={1}>
                            <Chip 
                              label={ref.status} 
                              size="small"
                              color={ref.status === 'completed' ? 'success' : 'warning'}
                            />
                            <Typography variant="caption">
                              {new Date(ref.created_at).toLocaleDateString()}
                            </Typography>
                            {ref.status === 'completed' && (
                              <Typography variant="caption" color="success.main" fontWeight={600}>
                                +₹{ref.creditsAwarded}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
